/**
 * Reveal-on-view: adds the class `is-in-view` to any element marked with
 * `data-reveal-on-view` the first time it intersects the viewport. Use the
 * class to flip CSS animations from `paused` to `running`, or to swap any
 * other style on entrance.
 *
 * @example
 *   <section class="hero" data-reveal-on-view>...</section>
 *
 *   .hero__item { animation: slide-in 0.7s both paused; }
 *   .hero.is-in-view .hero__item { animation-play-state: running; }
 *
 * Optional per-element knobs (data-* attributes):
 *   data-reveal-threshold="0.3"            // 0..1, default 0.15
 *   data-reveal-root-margin="0px 0px -10%" // any rootMargin string
 *
 * Picks up nodes inserted after load (theme editor section re-renders, etc.)
 * via a single MutationObserver. Observers are de-duplicated per
 * (threshold, rootMargin) so many sections share one IntersectionObserver.
 */

const SELECTOR = '[data-reveal-on-view]';
const REVEAL_CLASS = 'is-in-view';
const ATTACHED_FLAG = 'revealOnViewAttached';

/** @type {Map<string, IntersectionObserver>} */
const observerCache = new Map();

/**
 * @param {{ threshold: number, rootMargin: string }} options
 * @returns {IntersectionObserver}
 */
function getObserver(options) {
  const key = `${options.threshold}|${options.rootMargin}`;
  let observer = observerCache.get(key);
  if (observer) return observer;

  observer = new IntersectionObserver((entries, obs) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add(REVEAL_CLASS);
      obs.unobserve(entry.target);
    }
  }, options);

  observerCache.set(key, observer);
  return observer;
}

/**
 * @param {Element} element
 */
function attach(element) {
  if (!(element instanceof HTMLElement)) return;
  if (element.dataset[ATTACHED_FLAG] === 'true') return;
  element.dataset[ATTACHED_FLAG] = 'true';

  if (!('IntersectionObserver' in window)) {
    element.classList.add(REVEAL_CLASS);
    return;
  }

  const threshold = parseFloat(element.dataset.revealThreshold ?? '');
  const rootMargin = element.dataset.revealRootMargin || '0px';

  getObserver({
    threshold: Number.isFinite(threshold) ? threshold : 0.15,
    rootMargin,
  }).observe(element);
}

/**
 * @param {ParentNode} root
 */
function scan(root) {
  if (typeof root.querySelectorAll !== 'function') return;
  for (const element of root.querySelectorAll(SELECTOR)) {
    attach(element);
  }
}

scan(document);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== 1) continue;
      const element = /** @type {Element} */ (node);
      if (element.matches?.(SELECTOR)) attach(element);
      scan(element);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
